import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {clsx} from 'clsx'
import {Button, Tooltip} from '@primer/react'
import {useCallback, useEffect, useRef} from 'react'
import styles from './PullRequestMiniHeader.module.css'
import {StateIcon} from './StateIcon'
import {useHeaderPageData} from '../page-data/loaders/use-header-page-data'
import type {HeaderPageData} from '../page-data/payloads/header'
import {getRouteConfig} from '../routes/route-manager'
import {useMergeStatusButtonData, type ValidatedMergeStatusButtonData} from '../hooks/use-merge-status-button-data'
import {DockedPanel, useIsDockedPanelOpen, useDockedPanelActions} from '../contexts/DockedPanelContext'
import {MergeStatusButtonWrapper} from '@github-ui/mergebox'
import {OpenCommentsPanelButton} from './toolbar/OpenCommentsPanelButton'
import type {PullRequestHeaderProps} from './PullRequestHeader'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useFeatureState} from '../hooks/use-feature-state'
import {FeatureState} from '../page-data/payloads/header'
import {isLoggedIn, getCurrentUserLogin} from '@github-ui/client-env'
import {ReviewMenuButton} from './toolbar/ReviewMenuButton'
import {OpenCopilotChatPanelButton} from './toolbar/OpenCopilotChatPanelButton'
import {OpenDockedPanelButton} from './toolbar/OpenDockedPanelButton'
import {RefreshButton} from './toolbar/RefreshButton'
import {useIsComparisonOutdated} from '../stores/layout-selectors'
import {MiniHeaderTabSwitcher} from './MiniHeaderTabSwitcher'
import {HeaderDivider} from './HeaderDivider'
import {StackIcon} from '@primer/octicons-react'
import {useLayoutRouteData} from '../page-data/loaders/use-layout-route-data'
import {PullRequestHeaderBranches} from './PullRequestHeaderBranches'

export type PullRequestMiniHeaderProps = PullRequestHeaderProps

export function PullRequestMiniHeader({activeRoute, ...initialData}: PullRequestMiniHeaderProps) {
  const {helpUrl} = useLayoutRouteData()
  const {data: headerData} = useHeaderPageData({...initialData, helpUrl})
  return <PullRequestMiniHeaderContent {...headerData} activeRoute={activeRoute} />
}

export type PullRequestMiniHeaderContentProps = Omit<HeaderPageData, 'helpUrl'> & {
  activeRoute: PullRequestHeaderProps['activeRoute']
}

export function PullRequestMiniHeaderContent({
  aliveChannels,
  activeRoute,
  pullRequest,
  repository,
  mergeStatusButtonData,
  stack,
  urls,
}: PullRequestMiniHeaderContentProps) {
  const currentUserLogin = getCurrentUserLogin()
  const isPullRequestStacksEnabled = useFeatureState('pullRequestStacks') === FeatureState.Enabled
  const {dockedPanelSupported} = getRouteConfig(activeRoute)
  const validatedMergeData = useMergeStatusButtonData(pullRequest.state, mergeStatusButtonData)
  const isDockedMode = !!dockedPanelSupported
  const dockedPanelActions = useDockedPanelActions()
  const mergeStatusButtonRef = useRef<HTMLButtonElement>(null)
  const hasValidatedMergeData = validatedMergeData !== null

  const handleToggleDockedMergePanel = useCallback(() => {
    dockedPanelActions.togglePanel(DockedPanel.MergeStatus, {returnFocusTo: mergeStatusButtonRef.current})
  }, [dockedPanelActions])

  const isMergeStatusPanelOpen = useIsDockedPanelOpen(DockedPanel.MergeStatus)

  useEffect(() => {
    if (isDockedMode && isMergeStatusPanelOpen && !hasValidatedMergeData) {
      dockedPanelActions.closePanel()
    }
  }, [isDockedMode, isMergeStatusPanelOpen, hasValidatedMergeData, dockedPanelActions])

  const showMarkerCounts = isFeatureEnabled('prs_header_marker_counts')

  return (
    <div className={styles.miniHeaderInner}>
      <MiniHeaderTabSwitcher activeRoute={activeRoute} pullRequest={pullRequest} repository={repository} urls={urls} />
      <HeaderDivider />
      <div className={styles.metadata}>
        <StateIcon state={pullRequest.state} />
        <span className={clsx(styles.number, 'f5 fgColor-muted')}>#{pullRequest.number}</span>
        <a href="#top" className={clsx(styles.title, 'f5 text-bold fgColor-default')}>
          <VerifiedHTMLText className="markdown-title" html={pullRequest.titleHtml} />
        </a>
        <PullRequestHeaderBranches
          baseBranch={pullRequest.baseBranch}
          baseRepositoryDefaultBranch={repository.defaultBranch}
          baseRepositoryName={repository.name}
          baseRepositoryOwnerLogin={repository.ownerLogin}
          compactBranches
          headBranch={pullRequest.headBranch}
          headRepositoryOwnerLogin={pullRequest.headRepositoryOwnerLogin}
          headRepositoryName={pullRequest.headRepositoryName}
          isInAdvisoryRepo={pullRequest.isInAdvisoryRepo}
          stack={stack}
        />
      </div>
      <div className={styles.actions}>
        {validatedMergeData && (
          <MiniHeaderMergeOrRefresh
            validatedMergeData={validatedMergeData}
            isDockedMode={isDockedMode}
            onToggleDockedPanel={handleToggleDockedMergePanel}
            mergeStatusButtonRef={mergeStatusButtonRef}
            pullRequestAliveChannel={aliveChannels.pullRequest}
          />
        )}
        <OpenCommentsPanelButton
          className="hide-sm hide-md"
          buttonClasses={!showMarkerCounts ? styles.squareButton : undefined}
          showMarkerCounts={showMarkerCounts}
          size="medium"
        />
        {isFeatureEnabled('copilot_chat_docked_panel') && (
          <OpenCopilotChatPanelButton size="medium" variant="invisible" className="hide-sm hide-md" />
        )}
        <HeaderDivider className="hide-sm" />
        {currentUserLogin && (
          <ReviewMenuButton
            className="tmp-ml-2"
            currentUserLogin={currentUserLogin}
            pullRequest={pullRequest}
            headRefOid={pullRequest.headSha}
            repository={repository}
            overlayPortal="external"
            redirectOnMutation={activeRoute !== 'conversation'}
            stackBaseBranch={isPullRequestStacksEnabled ? stack?.baseBranch : undefined}
            size="medium"
          />
        )}
        <OpenDockedPanelButton size="medium" className="tmp-ml-2" />
        {isFeatureEnabled('unified_batch_pr_comments') && (
          // TODO(copilot-discovery-team#795,#802): remove this opener once the sticky-header "Manage batch (N)"
          // button (copilot-discovery-team#802) lands.
          // Gated by `unified_batch_pr_comments` only — The yellow icon color flags this as a dev-only
          // affordance not shipped to customers.
          <Tooltip text="Open Manage batch panel (dev)" direction="sw" type="label">
            <Button
              leadingVisual={StackIcon}
              size="medium"
              variant="invisible"
              data-testid="manage-batch-dev-opener"
              className={styles.manageBatchDevOpener}
              onClick={() => dockedPanelActions.togglePanel(DockedPanel.ManageBatchReviewComments)}
            />
          </Tooltip>
        )}
      </div>
    </div>
  )
}

function MiniHeaderMergeOrRefresh({
  validatedMergeData,
  isDockedMode,
  onToggleDockedPanel,
  mergeStatusButtonRef,
  pullRequestAliveChannel,
}: {
  validatedMergeData: ValidatedMergeStatusButtonData | null
  isDockedMode: boolean
  onToggleDockedPanel: () => void
  mergeStatusButtonRef: React.RefObject<HTMLButtonElement | null>
  pullRequestAliveChannel: string
}) {
  const isComparisonOutdated = useIsComparisonOutdated()
  const {helpUrl} = useLayoutRouteData()

  return (
    <>
      {isLoggedIn() && !isComparisonOutdated && validatedMergeData && (
        <MergeStatusButtonWrapper
          channels={validatedMergeData.channels}
          headSha={validatedMergeData.headSha}
          helpUrl={helpUrl}
          viewerLogin={validatedMergeData.viewerLogin}
          isDockedMode={isDockedMode}
          onToggleDockedPanel={onToggleDockedPanel}
          mergeStatusButtonRef={mergeStatusButtonRef}
          variant="invisible"
          size="medium"
          buttonClasses={clsx('hide-sm hide-md hide-lg', styles.hideLgPlus)}
          showTrailingArrow
        />
      )}
      <RefreshButton aliveChannel={pullRequestAliveChannel} />
    </>
  )
}
